import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import axiosClient from './axiosClient';
import { setTenantHome } from './runtimeTenantHome';
import { useEnvOptional } from '../context/EnvContext';
import { useIsEditable } from '../context/useIsEditable';

/**
 * Per-tenant editing-environment (home) metadata.
 *
 * Reads GET /api/tenant/:code/editability:
 *   { tenantCode, homeEnv, currentEnv, editableHere, isDefaultHome, migratingTo }
 *
 * EDITABILITY IS ENV-LEVEL ONLY — per-tenant authoring HOMES are RETIRED.
 * -----------------------------------------------------------------------
 * dev01 is the single editable env for EVERY tenant (EnvContext.editableEnv is
 * always dev01, on every domain). We therefore NO LONGER honor the backend's
 * per-tenant `editableHere` verdict for gating: served by a non-dev01 portal
 * (e.g. dosa.qa), that verdict reported tenants as "homed elsewhere" and forced
 * the whole tenant read-only even with dev01 selected. `editableHere` is now
 * ALWAYS the env-level `useIsEditable()` bit, so every downstream consumer
 * (useActiveTenantEditable, usePromoteGating, usePublishGate, the banner)
 * decides on env alone.
 *
 * `homeEnv` / `migratingTo` from the backend are still surfaced — for DISPLAY
 * and for cross-env request routing (setTenantHome → crossEnvRequestRouter, so
 * home-authoritative reads route browser-direct). They do NOT gate writes.
 *
 * GRACEFUL DEGRADE (endpoint absent / 404): same result — editableHere mirrors
 * the env-level bit, isDefaultHome=true.
 */
export interface TenantEditability {
  homeEnv: string;
  currentEnv: string;
  editableHere: boolean;
  isDefaultHome: boolean;
  migratingTo: string | null;
  /** True while the query is in flight (and we have no cached value yet). */
  isLoading: boolean;
  /** True when the backend errored in a way OTHER than a graceful 404 degrade. */
  isError: boolean;
}

interface EditabilityResponse {
  tenantCode: string;
  homeEnv: string;
  currentEnv: string;
  editableHere: boolean;
  isDefaultHome: boolean;
  migratingTo: string | null;
}

/** A 404 (or 501) means the per-tenant backend isn't there — degrade, not error. */
function isRouteAbsent(err: unknown): boolean {
  const status = (err as { response?: { status?: number } } | undefined)?.response?.status;
  return status === 404 || status === 501;
}

export function useTenantEditability(tenantCode: string | undefined): TenantEditability {
  // Non-throwing env read: a write-affordance can render outside an EnvProvider
  // (picker / unit test). currentEnv falls back to '' there; the degrade path
  // below returns editableHere=envEditable (which is true with no provider).
  const currentEnv = useEnvOptional()?.currentEnv ?? '';
  // Env-level fallback — the SOURCE OF TRUTH when the per-tenant backend is off.
  const envEditable = useIsEditable();

  const query = useQuery<EditabilityResponse, unknown>({
    // Keyed by env too: editableHere depends on which env we're browsing, so a
    // pinned `staleTime: Infinity` would be wrong. Refetch on env switch.
    queryKey: ['tenant-editability', currentEnv, tenantCode],
    enabled: !!tenantCode,
    queryFn: async () => {
      const { data } = await axiosClient.get<EditabilityResponse>(
        `/api/tenant/${encodeURIComponent(tenantCode as string)}/editability`,
      );
      return data;
    },
    staleTime: 15_000,
    // Don't retry a "route absent" degrade; retry a single transient error.
    retry: (failureCount, err) => {
      if (isRouteAbsent(err)) return false;
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 1;
    },
  });

  // Bridge the resolved per-tenant home into the synchronous axios router, so
  // home-authoritative promotion reads route browser-direct to dosa.<tenant home>
  // instead of the portal you're on (crossEnvRequestRouter reads this map).
  useEffect(() => {
    if (tenantCode && query.data?.homeEnv) {
      setTenantHome(tenantCode, query.data.homeEnv);
    }
  }, [tenantCode, query.data?.homeEnv]);

  // Graceful degrade: no tenant, route absent, or still-loading-with-no-data →
  // fall back to env-level editability so flag-off behaves exactly like today.
  const routeAbsent = isRouteAbsent(query.error);
  if (!tenantCode || routeAbsent || (!query.data && (query.isLoading || query.isError))) {
    return {
      homeEnv: currentEnv,
      currentEnv,
      editableHere: envEditable,
      isDefaultHome: true,
      migratingTo: null,
      isLoading: !!tenantCode && query.isLoading && !routeAbsent,
      // A genuine (non-degrade) error still surfaces isError so callers can
      // distinguish, but editability already fell back to env-level (safe).
      isError: query.isError && !routeAbsent,
    };
  }

  const data = query.data!;
  return {
    homeEnv: data.homeEnv,
    currentEnv: data.currentEnv,
    // Per-tenant homes retired: editability is env-level ONLY. We ignore the
    // backend's `data.editableHere` (stale "homed elsewhere" on non-dev01
    // portals) and report the env-level bit so dev01 is editable everywhere.
    editableHere: envEditable,
    isDefaultHome: data.isDefaultHome,
    migratingTo: data.migratingTo ?? null,
    isLoading: false,
    isError: false,
  };
}

export default useTenantEditability;
